import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import { validateOfferToken, respondToReformulation } from "../services/api";
import { FONTS, COLORS, BREAKPOINTS } from "../utils/styleConstants";
import SidebarLogo from "../assets/sidebar_logo.svg";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import LoadingIndicator from "../components/LoadingIndicator";
import NotificationMessage from "../components/NotificationMessage";
import OfferDisplay from "../components/oferta/OfferDisplay";

const DARK_BLUE = "#20376e";
const ACCENT_ORANGE = "#ff7057";

// Main container styles
const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: var(--secondary-color);
  display: flex;
  box-shadow: var(--box-shadow);
  overflow: hidden;
  height: 100vh;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 3rem;
  border-radius: 1rem 0 0 1rem;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: white;
  }
`;

const SidebarLogoStyled = styled.img`
  width: 40px;
  height: auto;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    width: 30px;
    margin-bottom: 0;
    margin-left: 1rem;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TwoColumnLayout = styled.div`
  display: flex;
  gap: 2rem;
  flex: 1;
  
  @media (max-width: ${BREAKPOINTS.TABLET}) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  max-width: 50%;
  
  @media (max-width: ${BREAKPOINTS.TABLET}) {
    max-width: 100%;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  max-width: 50%;
  
  @media (max-width: ${BREAKPOINTS.TABLET}) {
    max-width: 100%;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0 0 0.5rem 0;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 1.8rem;
  }
`;

const Description = styled.p`
  color: ${ACCENT_ORANGE};
  font-size: 1.1rem;
  font-family: ${FONTS.PRIMARY};
  margin-top: 0.5rem;
  margin-bottom: 0;
  line-height: 1.5;
  width: 100%;
  max-width: 70%;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    max-width: 100%;
    font-size: 1rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 600;
  min-height: 2.5em;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SectionDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  font-family: ${FONTS.SECONDARY};
  line-height: 1.4;
`;

const ReformulationContent = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const ReformulationText = styled.div`
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${COLORS.SECONDARY};
  white-space: pre-line;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  &:focus {
    outline: none;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AcceptButton = styled(Button)`
  background-color: #28a745;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #218838;
  }
`;

const RejectButton = styled(Button)`
  background-color: #dc3545;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #c82333;
  }
`;

const BackButton = styled(Button)`
  background-color: white;
  color: #a8b9d5;
  border: 1px solid #dbe4f2;
  
  &:hover:not(:disabled) {
    background-color: #f5f8fd;
  }
`;

const ConfirmationModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const ConfirmationContent = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const ConfirmationText = styled.p`
  color: #333;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const ReformulationResponsePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null); // 'accept' or 'reject'
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    const fetchOfferData = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");
        
        if (!token) {
          setError({
            title: "Token no encontrado",
            message: "No se ha proporcionado un token válido para acceder a la reformulación."
          });
          return;
        }
        
        const response = await validateOfferToken(token);
        
        if (response && response.valid) {
          const offerData = {
            ...response.offer,
            token
          };
          
          setOffer(offerData);
          
          // Check authentication
          if (!isAuthenticated) {
            sessionStorage.setItem("pendingOfferToken", token);
            sessionStorage.setItem("pendingOfferAction", "respond_reformulation");
            sessionStorage.setItem("pendingRedirectUrl", `/offers/reformulation?token=${token}`);
            navigate("/login", { state: { from: location } });
            return;
          }
        } else {
          setError({
            title: "Reformulación no disponible",
            message: response?.message || "No se puede acceder a esta reformulación."
          });
        }
      } catch (err) {
        console.error("Error fetching offer:", err);
        setError({
          title: "Error al cargar la reformulación",
          message: "Ha ocurrido un error al intentar cargar los detalles de la reformulación."
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchOfferData();
  }, [location.search, isAuthenticated, navigate]);

  const handleResponse = (responseType) => {
    setConfirmationType(responseType);
    setShowConfirmation(true);
  };

  const handleConfirmResponse = async () => {
    setIsProcessing(true);
    setShowConfirmation(false);
    
    try {
      await respondToReformulation(offer.id, confirmationType);
      
      const actionText = confirmationType === 'accept' ? 'aceptada' : 'rechazada';
      setSuccessMessage({
        title: `Reformulación ${actionText}`,
        message: `La reformulación ha sido ${actionText} correctamente.`
      });
      
      // Redirigir a "Mis Expedientes" después de 3 segundos
      setTimeout(() => {
        navigate("/offers");
      }, 3000);
    } catch (error) {
      setError({
        title: "Error al responder reformulación",
        message: error.message || "Ha ocurrido un error al intentar responder a la reformulación."
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Sidebar>
          <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
        </Sidebar>
        <ContentArea>
          <LoadingIndicator message="Cargando detalles de la reformulación..." />
        </ContentArea>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Sidebar>
          <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
        </Sidebar>
        <ContentArea>
          <NotificationMessage 
            title={error.title} 
            message={error.message} 
            isError={true} 
          />
        </ContentArea>
      </PageContainer>
    );
  }

  if (successMessage) {
    return (
      <PageContainer>
        <Sidebar>
          <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
        </Sidebar>
        <ContentArea>
          <NotificationMessage 
            title={successMessage.title} 
            message={successMessage.message} 
            isError={false} 
          />
        </ContentArea>
      </PageContainer>
    );
  }

  if (!offer) {
    return (
      <PageContainer>
        <Sidebar>
          <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
        </Sidebar>
        <ContentArea>
          <NotificationMessage 
            title="Reformulación no disponible" 
            message="No se ha podido cargar la información de la reformulación." 
            isError={true} 
          />
        </ContentArea>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Sidebar>
        <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
      </Sidebar>
      
      <ContentArea>
        <ContentContainer>
          <HeaderContainer>
            <Title>Responder a Reformulación</Title>
            <Description>
              El oferente ha reformulado su oferta en respuesta a tu contraoferta. 
              Revisa los cambios y decide si aceptas o rechazas la reformulación.
            </Description>
          </HeaderContainer>

          <TwoColumnLayout>
            <LeftColumn>
              <OfferDisplay offer={offer} />
            </LeftColumn>
            
            <RightColumn>
              <FormSection>
                <SectionTitle>Reformulación de la oferta</SectionTitle>
                <SectionDescription>
                  El oferente ha modificado su oferta original. Revisa el contenido de la reformulación:
                </SectionDescription>
                
                <ReformulationContent>
                  <ReformulationText>
                    {offer.reformulation_content || offer.reformulationText || "No hay contenido de reformulación disponible."}
                  </ReformulationText>
                </ReformulationContent>

                <ButtonContainer>
                  <BackButton onClick={handleBack} disabled={isProcessing}>
                    Atrás
                  </BackButton>
                  <RejectButton 
                    onClick={() => handleResponse('reject')} 
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Procesando..." : "Rechazar"}
                  </RejectButton>
                  <AcceptButton 
                    onClick={() => handleResponse('accept')} 
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Procesando..." : "Aceptar"}
                  </AcceptButton>
                </ButtonContainer>
              </FormSection>
            </RightColumn>
          </TwoColumnLayout>
        </ContentContainer>
        
        <Footer />
      </ContentArea>

      {showConfirmation && (
        <ConfirmationModal>
          <ConfirmationContent>
            <ConfirmationText>
              ¿Está seguro de que desea {confirmationType === 'accept' ? 'aceptar' : 'rechazar'} esta reformulación?
            </ConfirmationText>
            <ConfirmationButtons>
              <BackButton onClick={() => setShowConfirmation(false)}>
                Cancelar
              </BackButton>
              <AcceptButton onClick={handleConfirmResponse}>
                Confirmar
              </AcceptButton>
            </ConfirmationButtons>
          </ConfirmationContent>
        </ConfirmationModal>
      )}
    </PageContainer>
  );
};

export default ReformulationResponsePage; 